import { useEffect, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, AlertCircle, TrendingDown } from 'lucide-react';
import { adminApi, type FunnelStats } from '@/lib/api';
import { cn } from '@/lib/utils';
import { SignupsChart } from '@/components/AdminFunnel/SignupsChart';

const EVENT_LABELS: { key: string; label: string }[] = [
  { key: 'user.signup', label: 'Signups' },
  { key: 'user.login', label: 'Logins (returning)' },
  { key: 'domain.created', label: 'Domains added' },
  { key: 'domain.verified', label: 'Domains verified' },
  { key: 'domain.verify_failed', label: 'Verify failures' },
  { key: 'sitemap.generated', label: 'Sitemaps generated' },
];

function pct(part: number, whole: number): string {
  if (whole <= 0) return '—';
  return `${Math.round((part / whole) * 100)}%`;
}

// Page-scoped dark mode: follow the OS theme without touching the rest of the app.
function useSystemDark(): boolean {
  const [isDark, setIsDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches
  );
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e: MediaQueryListEvent) => setIsDark(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return isDark;
}

export function AdminFunnel() {
  const isDark = useSystemDark();
  const { data, isLoading, error } = useQuery<FunnelStats>({
    queryKey: ['admin-funnel'],
    queryFn: adminApi.getFunnel,
    retry: false,
  });

  // Full-bleed wrapper: cancels the layout's p-6 so the dark surface fills the
  // content area. `dark` on this ancestor is what activates the dark: variants.
  const shell = (children: ReactNode) => (
    <div className={cn('-m-6 p-6 min-h-screen', isDark && 'dark bg-slate-950')}>{children}</div>
  );

  if (isLoading) {
    return shell(
      <div className="flex items-center justify-center py-24 text-slate-500 dark:text-slate-400">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading funnel…
      </div>
    );
  }

  if (error || !data) {
    return shell(
      <div className="max-w-2xl">
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3 text-red-700 dark:bg-red-950/40 dark:border-red-900 dark:text-red-300">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium">Couldn’t load the funnel</p>
            <p className="text-sm">{error instanceof Error ? error.message : 'This page is admin-only.'}</p>
          </div>
        </div>
      </div>
    );
  }

  const { funnel, paying, signupsByDay, events30d, generatedAt } = data;
  const planOrder = ['starter', 'pro', 'business', 'enterprise'];
  const steps = [
    { label: 'Signed up', value: funnel.signups },
    { label: 'Added a domain', value: funnel.addedDomain },
    { label: 'Verified a domain', value: funnel.verifiedDomain },
    { label: 'Generated a sitemap', value: funnel.generatedSitemap },
  ];
  const top = steps[0].value;

  return shell(
    <div className="max-w-3xl space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">Activation Funnel</h1>
        <p className="text-slate-500 dark:text-slate-400 mt-1 text-sm">
          External users only (excludes the admin account). Updated {new Date(generatedAt).toLocaleString()}.
        </p>
      </div>

      {/* Signups over time */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="font-medium text-slate-800 dark:text-slate-200 mb-4">Signups — last 30 days</h2>
        <SignupsChart data={signupsByDay} />
      </div>

      {/* Lifetime funnel */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="font-medium text-slate-800 dark:text-slate-200 mb-5">Lifetime</h2>
        <div className="space-y-4">
          {steps.map((step, i) => {
            const prev = i === 0 ? null : steps[i - 1];
            const barWidth = top > 0 ? Math.max(2, (step.value / top) * 100) : 0;
            return (
              <div key={step.label}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-slate-700 dark:text-slate-300">{step.label}</span>
                  <span className="font-mono text-slate-900 dark:text-slate-100">
                    {step.value}
                    {prev && (
                      <span className="ml-2 text-xs text-slate-400 dark:text-slate-500 inline-flex items-center gap-0.5">
                        <TrendingDown className="w-3 h-3" />
                        {pct(step.value, prev.value)} of prev
                      </span>
                    )}
                  </span>
                </div>
                <div className="h-3 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-primary rounded-full transition-all"
                    style={{ width: `${barWidth}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-5">
          Overall signup → activation: <span className="font-mono">{pct(funnel.generatedSitemap, top)}</span>
        </p>
      </div>

      {/* Paying customers */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="font-medium text-slate-800 dark:text-slate-200 mb-5">Paying customers</h2>
        <div className="flex flex-wrap items-end gap-8">
          <div>
            <div className="text-3xl font-semibold text-slate-900 dark:text-slate-100 font-mono">{paying.total}</div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">
              paying · {pct(paying.total, top)} of signups
            </div>
          </div>
          <div>
            <div className="text-3xl font-semibold text-slate-900 dark:text-slate-100 font-mono">
              ${paying.estimatedMrrUsd.toLocaleString()}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">est. MRR</div>
          </div>
          <div className="flex flex-wrap gap-2">
            {planOrder.map((plan) => (
              <span
                key={plan}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800 px-3 py-1 text-xs text-slate-600 dark:text-slate-300"
              >
                <span className="capitalize">{plan}</span>
                <span className="font-mono font-semibold text-slate-900 dark:text-slate-100">{paying.byPlan[plan] ?? 0}</span>
              </span>
            ))}
          </div>
        </div>
        <p className="text-xs text-slate-400 dark:text-slate-500 mt-4">
          Excludes the admin account. Enterprise is custom-priced (counted, but $0 in the estimate).
        </p>
      </div>

      {/* Last-30-day events */}
      <div className="bg-white dark:bg-slate-900 rounded-lg border border-slate-200 dark:border-slate-800 p-6">
        <h2 className="font-medium text-slate-800 dark:text-slate-200 mb-1">Last 30 days</h2>
        <p className="text-xs text-slate-400 dark:text-slate-500 mb-5">
          From event logging — only captures activity since instrumentation went live.
        </p>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {EVENT_LABELS.map(({ key, label }) => (
            <div key={key} className="rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-4">
              <div className="text-2xl font-semibold text-slate-900 dark:text-slate-100 font-mono">{events30d[key] ?? 0}</div>
              <div className="text-xs text-slate-500 dark:text-slate-400 mt-1">{label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
